export const PropertyType = {
    RURAL: 'RURAL',
    URBAN: 'URBAN',
} as const satisfies Record<string, string>;

export type PropertyType = typeof PropertyType[keyof typeof PropertyType];

export const AreaUnit = {
    HECTARE: 'HECTARE',
    SQUARE_METER: 'SQUARE_METER',
} as const satisfies Record<string, string>;

export type AreaUnit = typeof AreaUnit[keyof typeof AreaUnit];

export const LegalRegime = {
    AF: 'AF',
    PLEDGE: 'PLEDGE',
    MORTGAGE: 'MORTGAGE',
    FIDUCIARY_ASSIGNMENT: 'FIDUCIARY_ASSIGNMENT',
} as const satisfies Record<string, string>;

export type LegalRegime = typeof LegalRegime[keyof typeof LegalRegime];

export const CostType = {
    REAL_ESTATE_REGISTRY: 'REAL_ESTATE_REGISTRY',
    REAL_ESTATE_APPRAISAL: 'REAL_ESTATE_APPRAISAL',
    REAL_ESTATE_RTD: 'REAL_ESTATE_RTD',
} as const satisfies Record<string, string>;

export type CostType = typeof CostType[keyof typeof CostType];

export interface PropertyOwner {
    personId: string;
    name: string;
    document: string;
}

export interface PropertyCost {
    id?: number;
    costType: CostType;
    value: number;
    costDate: string;
    registrationNumber?: string;
    registrationDate?: string;
    description?: string;
}

export interface PropertyValuation {
    id?: number;
    forcedLiquidationValue: number;
    valuationDate: string;
    current?: boolean;
}

export interface PropertyObligation {
    creditOperationId: string;
    allocatedPercentage: number | null;
    status: string;
    statusFormatted?: string;
}

export interface PropertyListItem {
    id: string;
    name: string;
    registrationNumber: string | null;
    registryOffice: string;
    registryState: string;
    propertyType: PropertyType;
    propertyTypeFormatted: string;
    legalRegime: LegalRegime;
    legalRegimeFormatted: string;
    area: {
        areaValue: number | null;
        areaUnit: AreaUnit | null;
        areaUnitFormatted: string;
    };
    location: string | null;
    appraised: boolean;
    archived: boolean;
    discharged: boolean;
    statusFormatted: string;
    monitoringEnabled: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface PropertyDetail extends PropertyListItem {
    description: string | null;
    incraCode: string | null;
    carCode: string | null;
    comments: string | null;
    owners: PropertyOwner[];
    costs: PropertyCost[];
    valuations: PropertyValuation[];
    obligations: PropertyObligation[];
    dischargeDate: string | null;
    dischargeValue: number | null;
    monitoringEnabledFormatted: string;
    monitoringLastDate: string | null;
}

export const PROPERTY_TYPE_OPTIONS = [
    { value: PropertyType.RURAL, label: 'Rural' },
    { value: PropertyType.URBAN, label: 'Urbano' },
];

export const AREA_UNIT_OPTIONS = [
    { value: AreaUnit.HECTARE, label: 'Hectares' },
    { value: AreaUnit.SQUARE_METER, label: 'm²' },
];

export const LEGAL_REGIME_OPTIONS = [
    { value: LegalRegime.AF, label: 'Alienação Fiduciária' },
    { value: LegalRegime.PLEDGE, label: 'Penhor' },
    { value: LegalRegime.MORTGAGE, label: 'Hipoteca' },
    { value: LegalRegime.FIDUCIARY_ASSIGNMENT, label: 'Cessão Fiduciária' },
];

export const COST_TYPE_OPTIONS = [
    { value: CostType.REAL_ESTATE_REGISTRY, label: 'Cartório' },
    { value: CostType.REAL_ESTATE_APPRAISAL, label: 'Laudo de avaliação' },
    { value: CostType.REAL_ESTATE_RTD, label: 'RTD - Registro de Títulos e Documentos' },
];

// Maximum number of owners a property can have. Single source of truth,
// reused by the form (UI) and the page-form hook (validation) to avoid drift.
export const MAX_OWNERS = 20;
